import { useEffect, useMemo, useState } from "react";
import { collection, getDocs, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../blog/blogFirebase";
import { computeClusterTrends, siteSeries } from "./seoTrends";

// Written by captureSeoSnapshot via the Admin SDK; admins have read access via
// firestore.rules. Exactly as useKeywordClusters and useKeywordRefresh do, this
// subscribes directly rather than paying for a callable round-trip.
const SNAPSHOTS_COLLECTION = "blog_seo_snapshots";

/**
 * ~2 years of weekly points.
 *
 * Fetched in full ONCE, then sliced client-side by the range selector. The
 * alternative — refetching per range — would tear down and rebuild the listener
 * on every click for no benefit: 104 small docs is a single cheap read, and
 * slicing locally makes switching range instant with no loading flash.
 *
 * This must stay >= the largest range in seoRanges.js, or a "12 months"
 * selection would silently render 26 weeks of data while claiming 52.
 */
const MAX_POINTS = 104;

/** Firestore Timestamp | ISO string | Date → Date | null. */
function toDate(value) {
  if (!value) return null;
  const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Live view of the snapshot series, oldest first.
 *
 * An empty collection is surfaced as an empty list rather than an error: before
 * the first capture (or backfill) there is simply nothing to show yet, which is
 * a valid state for a read-only screen.
 */
export function useSeoSnapshots(points = MAX_POINTS) {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    // Descending + limit gets the MOST RECENT n points; the series is then
    // reversed to chronological order for display. Ordering ascending with a
    // limit would return the oldest n instead, which is the opposite of useful.
    const q = query(
      collection(db, SNAPSHOTS_COLLECTION),
      orderBy("date", "desc"),
      limit(points)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs.map((d) => ({
          ...d.data(),
          id: d.id,
          capturedAt: toDate(d.data().capturedAt),
        }));
        setSnapshots(docs.reverse());
        setLoading(false);
        setError("");
      },
      (err) => {
        console.error("[useSeoSnapshots] snapshot error:", err);
        setError(err.message || "Could not read SEO snapshots.");
        setLoading(false);
      }
    );

    return () => unsub();
  }, [points]);

  const trends = useMemo(() => computeClusterTrends(snapshots), [snapshots]);
  const site = useMemo(() => siteSeries(snapshots), [snapshots]);
  const latest = snapshots.length ? snapshots[snapshots.length - 1] : null;

  return { snapshots, trends, site, latest, loading, error };
}

/**
 * Module-level cache of keyword rows, keyed by snapshot date.
 *
 * Lives outside React so it survives expand/collapse cycles and remounts. The
 * data is immutable once written (a snapshot describes a finished week), so a
 * cached read can never go stale — the only invalidation needed is a page
 * reload, which clears it naturally.
 */
const keywordCache = new Map();

/** Keyword rows for one date, cached. */
async function loadKeywords(date) {
  if (keywordCache.has(date)) return keywordCache.get(date);

  const snap = await getDocs(collection(db, SNAPSHOTS_COLLECTION, date, "keywords"));
  const rows = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
  keywordCache.set(date, rows);
  return rows;
}

/**
 * Keyword rows for two dates, compared.
 *
 * Deliberately a one-shot read rather than a subscription: this is up to two
 * subcollection reads of ~90 docs each, and the underlying data only changes
 * once a week, so a live listener would cost far more than it is worth.
 *
 * Fetches lazily — the caller passes dates only once a cluster is expanded, so
 * opening the screen costs nothing until a drill-down is actually requested.
 */
export function useKeywordComparison(fromDate, toDate2) {
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!fromDate || !toDate2) {
      setRows({});
      return undefined;
    }

    // Guards against a stale response landing after the dates change.
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        // A single-point range compares a date with itself; reading it twice
        // would double the cost for identical data.
        const dates = fromDate === toDate2 ? [fromDate] : [fromDate, toDate2];
        const results = await Promise.all(
          dates.map(async (date) => [date, await loadKeywords(date)])
        );
        if (!cancelled) setRows(Object.fromEntries(results));
      } catch (e) {
        console.error("[useKeywordComparison] read error:", e);
        if (!cancelled) setError(e.message || "Could not read keyword rows.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [fromDate, toDate2]);

  return { rows, loading, error };
}
